import React from 'react';
import { motion } from 'motion/react';
import { AlertTriangle } from 'lucide-react';

interface QuizExitDialogProps {
  onStay: () => void;
  onLeave: () => void;
  isSubmitting?: boolean;
}

export function QuizExitDialog({ onStay, onLeave, isSubmitting = false }: QuizExitDialogProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quiz-exit-dialog-title"
    >
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Stay on quiz" onClick={onStay} />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        className="relative w-full sm:max-w-md bg-[var(--color-surface-1)] rounded-t-[var(--radius-xl)] sm:rounded-[var(--radius-xl)] border border-[var(--color-border)] shadow-[var(--shadow-lg)]"
      >
        <div className="px-6 py-5 border-b border-[var(--color-border)]">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-[var(--radius-md)] bg-[var(--color-warning-subtle)] flex items-center justify-center shrink-0 text-[var(--color-warning)]">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h3 id="quiz-exit-dialog-title" className="text-base font-semibold text-[var(--color-text-primary)]">
                Leave this round?
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1 leading-relaxed">
                Leaving now will submit your current answers for this round. You will not be able to rejoin or change
                them afterward.
              </p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onStay}
            disabled={isSubmitting}
            className="px-4 py-2.5 rounded-[var(--radius-md)] text-sm font-medium bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] transition-colors duration-[var(--duration-fast)] disabled:opacity-60"
          >
            Keep playing
          </button>
          <button
            type="button"
            onClick={onLeave}
            disabled={isSubmitting}
            className="px-4 py-2.5 rounded-[var(--radius-md)] text-sm bg-[var(--color-surface-2)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors duration-[var(--duration-fast)] disabled:opacity-60"
          >
            {isSubmitting ? 'Submitting…' : 'Leave and submit answers'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
